import {useState, useEffect, useCallback} from "react";
import {useFeatureFlags} from "utilities/featureUtil";
import {searchCommand} from "utilities/helperFunctions";

/**
 * Manages slash-command template lookup and keyboard navigation.
 *
 * @param {Object} templates - Full list of templates to search.
 * @param {string} [messageContent=""] - Current editor/text input content.
 * @param {number} cursorPosition - Current cursor index.
 * @param {boolean} menuOpen - Indicates if the templates menu is open.
 * @param {function} setTemplatesData - Setter to notify parent about templatesData changes.
 * @param {function} onSelectCallback - Callback function to handle template selection.
 * @param {string} emailContent - Current email content.
 * @param {string} inputText - Current input text.
 * @param {function} insertText - Function to insert text at a specific position.
 * @param {React.RefObject} menuRef - Ref object for the templates menu.
 * @returns {{
 *   templateState: {
 *     search: string|null,
 *     results: [],
 *     selectedIdx: number,
 *     disabled: boolean,
 *     init: boolean
 *   },
 *   openTemplates: () => {},
 *   closeTemplates: () => {},
 *   handleKeyDown: (event) => {},
 *   scrollToItem: (index) => {}
 * }}
 */
export function useTemplateSearch(
  templates,
  messageContent = "",
  cursorPosition,
  menuOpen,
  setTemplatesData,
  onSelectCallback,
  emailContent,
  inputText,
  insertText,
  menuRef,
) {
  const {cannedResponses} = useFeatureFlags();
  const [templateState, setTemplateState] = useState({
    search: null,
    results: [],
    selectedIdx: -1,
    disabled: false,
    init: false,
  });

  const openTemplates = useCallback(() => {
    setTemplateState({
      search: "",
      results: templates,
      selectedIdx: -1,
      disabled: false,
      init: true,
    });
    setTemplatesData((d) => ({...d, open: true}));
  }, [templates, setTemplatesData]);

  const closeTemplates = useCallback(() => {
    setTemplateState((prev) => ({
      ...prev,
      search: null,
      results: [],
      selectedIdx: -1,
    }));
    setTemplatesData((d) => ({...d, open: false}));
  }, [setTemplatesData]);

  useEffect(() => {
    if (!cannedResponses || templateState.disabled) return;

    // Opened via button or slash
    if (templateState.init) {
      setTemplateState((prev) => ({
        ...prev,
        init: false,
        search: "",
        results: templates,
      }));

      // Insert space after slash if needed
      const nextChar = messageContent.slice(cursorPosition, cursorPosition + 1);
      if (nextChar && !/\s/.test(nextChar)) {
        insertText(" ", cursorPosition, cursorPosition);
      }
      return;
    }

    // If there's at least one slash in content (before cursor)
    if (messageContent.includes("/")) {
      const lastSlash = messageContent.lastIndexOf("/", cursorPosition - 1);

      if (lastSlash !== -1) {
        if (cursorPosition > lastSlash) {
          const target = messageContent.slice(lastSlash);
          const {results, searchText} = searchCommand(target, templates);
          const endAfter = lastSlash + searchText.length + 1;

          if (searchText !== templateState.search) {
            setTemplateState((prev) => ({
              ...prev,
              search: searchText,
              results,
              selectedIdx: results.length ? 0 : -1,
              disabled: false,
            }));

            // Open templates if not open and user continues typing after "/"
            if (
              !menuOpen &&
              (templateState.search || cursorPosition === endAfter)
            ) {
              setTemplatesData((d) => ({...d, open: true}));
            }
          } else if (cursorPosition > endAfter) {
            closeTemplates();
          }
          return;
        }
      }
    }
    // fallback: close
    if (menuOpen) closeTemplates();
  }, [emailContent, inputText]);

  const onSelect = useCallback(
    (item, enterKey) => {
      closeTemplates();
      setTemplateState((prev) => ({...prev, disabled: true}));
      onSelectCallback(item, enterKey, {
        search: templateState.search,
        cursorPosition: cursorPosition,
        messageContent: messageContent,
      });
    },
    [
      onSelectCallback,
      closeTemplates,
      templateState.search,
      cursorPosition,
      messageContent,
    ],
  );

  // Keyboard navigation & selection
  const scrollToItem = useCallback(
    (index) => {
      const menuItem =
        menuRef?.current?.querySelectorAll(":scope > div")[index];
      if (menuItem) {
        menuItem.scrollIntoView({block: "nearest", behavior: "smooth"});
      }
    },
    [menuRef],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "/") {
        openTemplates();
      }
      if (menuOpen && !templateState.disabled) {
        const {selectedIdx, results} = templateState;
        if (e.key === "ArrowDown") {
          e.preventDefault();
          const next = selectedIdx < results.length - 1 ? selectedIdx + 1 : 0;
          setTemplateState((prev) => ({...prev, selectedIdx: next}));
          scrollToItem(next);
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          const prev =
            selectedIdx > 0
              ? selectedIdx > results.length - 1
                ? results.length - 1
                : selectedIdx - 1
              : results.length - 1;
          setTemplateState((ts) => ({...ts, selectedIdx: prev}));
          scrollToItem(prev);
        } else if (
          e.key === "Enter" &&
          results[selectedIdx] &&
          results.length > 0
        ) {
          e.preventDefault();
          onSelect(results[selectedIdx], true);
        } else if (e.key === "Escape") {
          setTemplateState((prev) => ({...prev, disabled: true}));
          closeTemplates();
        } else if (e.key.length > 1 || !/^[A-Za-z0-9]$/.test(e.key)) {
          closeTemplates();
        }
      }
    },
    [
      menuOpen,
      openTemplates,
      closeTemplates,
      scrollToItem,
      onSelect,
      templateState,
    ],
  );

  return {
    templateState,
    openTemplates,
    closeTemplates,
    handleKeyDown,
    onSelect: (item) => onSelect(item, false),
  };
}
